<template>
  <div class="exam-result-page">
    <!-- 加载状态 -->
    <div v-if="loading" class="status-card">正在加载考试结果...</div>

    <!-- 错误状态 -->
    <div v-else-if="errorMessage" class="status-card is-error">
      {{ errorMessage }}
      <button class="btn btn--secondary" @click="$router.back()">返回</button>
    </div>

    <!-- 考试结果 -->
    <template v-else-if="result.id">
      <!-- 结果概览 -->
      <section class="result-overview panel">
        <div
          class="result-badge"
          :class="{
            'result-badge--pass': result.result === 1,
            'result-badge--fail': result.result === 2
          }"
        >
          <div class="result-icon">
            {{ result.result === 1 ? '🎉' : '💪' }}
          </div>
          <h1 class="result-title">
            {{ result.result === 1 ? '恭喜通过！' : '继续加油！' }}
          </h1>
          <p class="result-subtitle">{{ result.result_word }}</p>
        </div>

        <div class="result-score">
          <div class="score-main">
            <span class="score-value">{{ result.score }}</span>
            <span class="score-total">/ {{ result.total_score }}</span>
          </div>
          <div class="score-label">考试得分</div>
        </div>

        <div class="result-info">
          <div class="info-item">
            <span class="info-label">及格分数</span>
            <span class="info-value">{{ result.pass_score }} 分</span>
          </div>
          <div class="info-item">
            <span class="info-label">考试时长</span>
            <span class="info-value">{{ formatDuration(result.started_at, result.finished_at) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">完成时间</span>
            <span class="info-value">{{ formatDate(result.finished_at) }}</span>
          </div>
        </div>
      </section>

      <!-- 课程信息 -->
      <section class="result-course panel">
        <h2>考试信息</h2>
        <div class="course-info">
          <div class="course-item">
            <span class="course-label">课程名称</span>
            <span class="course-value">{{ result.course_title || '-' }}</span>
          </div>
          <div class="course-item">
            <span class="course-label">考试章节</span>
            <span class="course-value">{{ result.target_title || '-' }}</span>
          </div>
          <div class="course-item">
            <span class="course-label">考试类型</span>
            <span class="course-value">{{ result.target_type_word || '-' }}</span>
          </div>
        </div>
      </section>

      <QuestionResultDetails :answers="answerDetails" />

      <!-- 操作按钮 -->
      <section class="result-actions panel">
        <button class="btn btn--secondary" @click="goToCourse">
          返回课程
        </button>
        <button
          v-if="result.result === 2"
          class="btn btn--primary"
          @click="retakeExam"
        >
          重新考试
        </button>
        <button
          v-else
          class="btn btn--primary"
          @click="continueStudy"
        >
          继续学习
        </button>
      </section>
    </template>

    <!-- Token 失效弹窗 -->
    <AuthModal :visible="showAuthModal" @relogin="handleRelogin" />
  </div>
</template>

<script>
import { fetchExamResult } from '@/api/courseProcess'
import AuthModal from '@/components/AuthModal.vue'
import QuestionResultDetails from '@/components/question/QuestionResultDetails.vue'
import authErrorHandler from '@/mixins/authErrorHandler'

export default {
  name: 'ExamResultView',
  components: {
    AuthModal,
    QuestionResultDetails
  },
  mixins: [authErrorHandler],
  props: {
    recordId: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      loading: true,
      errorMessage: '',
      result: {}
    }
  },
  computed: {
    answerDetails() {
      return Array.isArray(this.result.answer_details) ? this.result.answer_details : []
    }
  },
  created() {
    this.loadResult()
  },
  methods: {
    async loadResult() {
      this.loading = true
      this.errorMessage = ''

      try {
        const data = await fetchExamResult(Number(this.recordId))
        this.result = data
      } catch (error) {
        console.error('加载考试结果失败:', error)
        if (this.isAuthError(error)) {
          this.handleAuthError()
        } else {
          this.errorMessage = error.message || '加载失败，请稍后重试'
        }
      } finally {
        this.loading = false
      }
    },
    formatDuration(startTime, endTime) {
      if (!startTime || !endTime) return '-'

      const start = new Date(startTime)
      const end = new Date(endTime)
      const seconds = Math.floor((end - start) / 1000)

      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      const secs = seconds % 60

      if (hours > 0) {
        return `${hours}小时${minutes}分钟`
      }
      if (minutes > 0) {
        return `${minutes}分${secs}秒`
      }
      return `${secs}秒`
    },
    formatDate(dateString) {
      if (!dateString) return '-'

      const date = new Date(dateString)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')

      return `${year}-${month}-${day} ${hours}:${minutes}`
    },
    goToCourse() {
      if (this.result.course_id) {
        this.$router.push({
          name: 'course-detail',
          params: { id: this.result.course_id }
        })
      } else {
        this.$router.push({ name: 'courses' })
      }
    },
    retakeExam() {
      if (this.result.process_exam_id) {
        this.$router.replace({
          name: 'exam',
          params: { processExamId: this.result.process_exam_id }
        })
      }
    },
    continueStudy() {
      this.goToCourse()
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/styles/tokens';

.exam-result-page {
  max-width: 900px;
  margin: 0 auto;
  padding: 20px;
}

.status-card {
  padding: 40px;
  text-align: center;
  background: #f5f7fa;
  border-radius: 12px;
  color: #606266;

  &.is-error {
    background: #fef0f0;
    color: #f56c6c;
  }

  .btn {
    margin-top: 16px;
  }
}

.panel {
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);

  h2 {
    font-size: 20px;
    font-weight: 600;
    margin: 0 0 20px 0;
    color: $color-text-primary;
  }
}

/* 结果概览 */
.result-overview {
  text-align: center;
}

.result-badge {
  margin-bottom: 32px;

  .result-icon {
    font-size: 80px;
    margin-bottom: 16px;
  }

  .result-title {
    font-size: 36px;
    font-weight: 700;
    margin: 0 0 8px 0;
  }

  .result-subtitle {
    font-size: 18px;
    margin: 0;
  }

  &.result-badge--pass {
    .result-title {
      color: #10b981;
    }

    .result-subtitle {
      color: #059669;
    }
  }

  &.result-badge--fail {
    .result-title {
      color: #f59e0b;
    }

    .result-subtitle {
      color: #d97706;
    }
  }
}

.result-score {
  margin-bottom: 32px;

  .score-main {
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 8px;
    margin-bottom: 8px;

    .score-value {
      font-size: 72px;
      font-weight: 800;
      color: $color-primary;
      line-height: 1;
    }

    .score-total {
      font-size: 32px;
      color: $color-text-secondary;
    }
  }

  .score-label {
    font-size: 16px;
    color: $color-text-secondary;
  }
}

.result-info {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  padding-top: 24px;
  border-top: 2px solid #e4e7ed;
}

.info-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  .info-label {
    font-size: 14px;
    color: $color-text-secondary;
  }

  .info-value {
    font-size: 16px;
    font-weight: 600;
    color: $color-text-primary;
  }
}

/* 课程信息 */
.course-info {
  display: grid;
  gap: 16px;
}

.course-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f5f7fa;
  border-radius: 8px;

  .course-label {
    font-size: 14px;
    color: $color-text-secondary;
  }

  .course-value {
    font-size: 15px;
    font-weight: 500;
    color: $color-text-primary;
  }
}

/* 操作按钮 */
.result-actions {
  display: flex;
  justify-content: center;
  gap: 16px;
}

@media (max-width: 768px) {
  .exam-result-page {
    padding: 12px;
  }

  .panel {
    padding: 20px;
  }

  .result-badge {
    .result-icon {
      font-size: 60px;
    }

    .result-title {
      font-size: 28px;
    }
  }

  .result-score {
    .score-main {
      .score-value {
        font-size: 56px;
      }

      .score-total {
        font-size: 24px;
      }
    }
  }

  .result-info {
    grid-template-columns: 1fr;
  }

  .result-actions {
    flex-direction: column;

    .btn {
      width: 100%;
    }
  }
}
</style>
